import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync } from "../../utilities/coalesceAsync";

const authHeaders = (token) => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const resolveList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const GetVacations = createAsyncThunk(
  "vacations/getAll",
  async ({ token, from, to } = {}, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();

      const data = await coalesceAsync(
        `get-vacations:${qs}`,
        async () => {
          const response = await fetch(
            `${config.apiUrl}/vacations${qs ? `?${qs}` : ""}`,
            { method: "GET", headers: authHeaders(token) }
          );
          const body = await response.json();
          if (!response.ok) {
            const error = new Error(body?.message ?? "Unable to fetch vacations");
            error.payload = body;
            throw error;
          }
          return body;
        },
        { ttlMs: 1500 }
      );

      return fulfillWithValue(data);
    } catch (error) {
      if (error?.payload) {
        return rejectWithValue(error.payload);
      }
      return rejectWithValue({ message: error?.message ?? "Unable to fetch vacations" });
    }
  }
);

export const GetWeekendDays = createAsyncThunk(
  "vacations/getWeekendDays",
  async (token, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const data = await coalesceAsync(
        "get-weekend-days",
        async () => {
          const response = await fetch(`${config.apiUrl}/vacations/weekend-days`, {
            method: "GET",
            headers: authHeaders(token),
          });
          const body = await response.json();
          if (!response.ok) {
            const error = new Error(body?.message ?? "Unable to fetch weekend days");
            error.payload = body;
            throw error;
          }
          return body;
        },
        { ttlMs: 1500 }
      );

      return fulfillWithValue(data);
    } catch (error) {
      if (error?.payload) {
        return rejectWithValue(error.payload);
      }
      return rejectWithValue({ message: error?.message ?? "Unable to fetch weekend days" });
    }
  }
);

export const UpdateWeekendDays = createAsyncThunk(
  "vacations/updateWeekendDays",
  async ({ token, weekend_days }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/vacations/weekend-days`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({ weekend_days }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Unable to update weekend days" });
    }
  }
);

export const GetCalendarClosedDays = createAsyncThunk(
  "vacations/getCalendarConfig",
  async ({ token, from, to }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const url = `${config.apiUrl}/vacations/calendar-config${qs ? `?${qs}` : ""}`;

      const data = await coalesceAsync(
        `get-calendar-config:${qs}`,
        async () => {
          const response = await fetch(url, {
            method: "GET",
            headers: authHeaders(token),
          });
          const body = await response.json();
          if (!response.ok) {
            const error = new Error(body?.message ?? "Unable to fetch calendar closed days");
            error.payload = body;
            throw error;
          }
          return body;
        },
        { ttlMs: 1500 }
      );

      return fulfillWithValue(data);
    } catch (error) {
      if (error?.payload) {
        return rejectWithValue(error.payload);
      }
      return rejectWithValue({ message: error?.message ?? "Unable to fetch calendar closed days" });
    }
  }
);

export const AddVacation = createAsyncThunk(
  "vacations/add",
  async ({ token, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/vacations`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Unable to create vacation" });
    }
  }
);

export const UpdateVacation = createAsyncThunk(
  "vacations/update",
  async ({ token, id, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/vacations/${id}`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Unable to update vacation" });
    }
  }
);

export const DeleteVacation = createAsyncThunk(
  "vacations/delete",
  async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/vacations/${id}`, {
        method: "DELETE",
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue({ ...data, id });
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Unable to delete vacation" });
    }
  }
);

const initialState = {
  vacations: [],
  weekendDays: [],
  loading: false,
  weekendLoading: false,
  error: null,
  successMessage: null,
};

const vacationsSlice = createSlice({
  name: "vacations",
  initialState,
  reducers: {
    clearVacationsMessage(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetVacations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetVacations.fulfilled, (state, action) => {
        state.loading = false;
        state.vacations = resolveList(action.payload);
      })
      .addCase(GetVacations.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Failed to load vacations");
        state.vacations = [];
      })
      .addCase(GetWeekendDays.pending, (state) => {
        state.weekendLoading = true;
      })
      .addCase(GetWeekendDays.fulfilled, (state, action) => {
        state.weekendLoading = false;
        const days = action.payload?.data?.weekend_days ?? action.payload?.weekend_days ?? [];
        state.weekendDays = Array.isArray(days) ? days.map(Number) : [];
      })
      .addCase(GetWeekendDays.rejected, (state, action) => {
        state.weekendLoading = false;
        state.error = formatApiError(action.payload, "Failed to load weekend days");
      })
      .addCase(UpdateWeekendDays.fulfilled, (state, action) => {
        const days = action.payload?.data?.weekend_days ?? [];
        state.weekendDays = Array.isArray(days) ? days.map(Number) : [];
        state.successMessage = action.payload?.message ?? "Weekend days updated";
      })
      .addCase(UpdateWeekendDays.rejected, (state, action) => {
        state.error = formatApiError(action.payload, "Failed to update weekend days");
      })
      .addCase(GetCalendarClosedDays.fulfilled, (state, action) => {
        const payload = action.payload?.data ?? {};
        state.weekendDays = Array.isArray(payload.weekend_days)
          ? payload.weekend_days.map(Number)
          : [];
        state.vacations = Array.isArray(payload.vacations) ? payload.vacations : [];
      })
      .addCase(AddVacation.fulfilled, (state, action) => {
        const created = action.payload?.data;
        if (created) state.vacations = [...state.vacations, created];
        state.successMessage = action.payload?.message ?? "Vacation created";
      })
      .addCase(AddVacation.rejected, (state, action) => {
        state.error = formatApiError(action.payload, "Failed to create vacation");
      })
      .addCase(UpdateVacation.fulfilled, (state, action) => {
        const updated = action.payload?.data;
        if (updated) {
          state.vacations = state.vacations.map((v) => (v.id === updated.id ? updated : v));
        }
        state.successMessage = action.payload?.message ?? "Vacation updated";
      })
      .addCase(UpdateVacation.rejected, (state, action) => {
        state.error = formatApiError(action.payload, "Failed to update vacation");
      })
      .addCase(DeleteVacation.fulfilled, (state, action) => {
        const id = action.payload?.id ?? action.meta?.arg?.id;
        state.vacations = state.vacations.filter((v) => v.id !== id);
        state.successMessage = action.payload?.message ?? "Vacation deleted";
      })
      .addCase(DeleteVacation.rejected, (state, action) => {
        state.error = formatApiError(action.payload, "Failed to delete vacation");
      });
  },
});

export const { clearVacationsMessage } = vacationsSlice.actions;
export default vacationsSlice.reducer;
